import React, { useState, useEffect } from 'react';
import { Calculator as CalculatorIcon, Ruler, Activity, RefreshCw, History, Scale } from 'lucide-react';
import { calculateAsphaltTonnage, isValidPositiveNumber, Calculation, UNITS, UnitSystem } from '../utils/calculationUtils';
import { CalculatorResults } from './CalculatorResults';
import { InputField } from './InputField';
import { CalculationHistory } from './CalculationHistory';

export const Calculator: React.FC = () => {
  const [width, setWidth] = useState<string>('');
  const [length, setLength] = useState<string>('');
  const [thickness, setThickness] = useState<string>('');
  const [tonnage, setTonnage] = useState<number>(0);
  const [hasCalculated, setHasCalculated] = useState<boolean>(false);
  const [unit, setUnit] = useState<UnitSystem>(UNITS.IMPERIAL);
  const [history, setHistory] = useState<Calculation[]>([]);
  const [formErrors, setFormErrors] = useState<{
    width?: string;
    length?: string;
    thickness?: string;
  }>({});

  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        const activeElement = document.activeElement;
        if (activeElement instanceof HTMLInputElement) {
          e.preventDefault();
          handleCalculate(new Event('submit') as any);
        }
      }
    };

    document.addEventListener('keypress', handleKeyPress);
    return () => document.removeEventListener('keypress', handleKeyPress);
  }, [width, length, thickness]);

  const validateForm = (): boolean => {
    const errors: { width?: string; length?: string; thickness?: string } = {};
    
    if (!width || !isValidPositiveNumber(width)) {
      errors.width = 'Please enter a valid positive number';
    }
    
    if (!length || !isValidPositiveNumber(length)) {
      errors.length = 'Please enter a valid positive number';
    }
    
    if (!thickness || !isValidPositiveNumber(thickness)) {
      errors.thickness = 'Please enter a valid positive number';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    const widthValue = parseFloat(width);
    const lengthValue = parseFloat(length);
    const thicknessValue = parseFloat(thickness);
    
    const result = calculateAsphaltTonnage(widthValue, lengthValue, thicknessValue, unit);
    setTonnage(result);
    setHasCalculated(true);

    // Add to history
    const calculation: Calculation = {
      id: Date.now().toString(),
      width: widthValue,
      length: lengthValue,
      thickness: thicknessValue,
      tonnage: result,
      timestamp: Date.now(),
      unit
    };
    setHistory(prev => [calculation, ...prev].slice(0, 10));
  };

  const handleClear = () => {
    setWidth('');
    setLength('');
    setThickness('');
    setTonnage(0);
    setHasCalculated(false);
    setFormErrors({});
  };

  const toggleUnit = () => {
    setUnit(prev => prev === UNITS.IMPERIAL ? UNITS.METRIC : UNITS.IMPERIAL);
    handleClear();
  };

  return (
    <div className="w-full max-w-4xl">
      <div className="bg-white rounded-xl shadow-xl overflow-hidden transition-all duration-300 transform hover:shadow-2xl">
        <div className="bg-slate-800 text-white p-6">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center space-x-3">
              <CalculatorIcon className="h-6 w-6 text-yellow-400" />
              <h2 className="text-2xl font-bold">Asphalt Calculator</h2>
            </div>
            <button
              onClick={toggleUnit}
              className="flex items-center space-x-2 bg-slate-700 hover:bg-slate-600 text-white px-4 py-2 rounded-md transition-colors duration-300"
            >
              <Scale className="h-4 w-4" />
              <span>{unit === UNITS.IMPERIAL ? 'Imperial' : 'Metric'}</span>
            </button>
          </div>
          <p className="text-slate-300">
            Estimate the asphalt pavement tonnage required for your construction project
          </p>
        </div>
        
        <div className="p-6 md:p-8">
          <div className="bg-slate-100 p-4 rounded-lg mb-8">
            <div className="flex items-center text-slate-700 mb-2">
              <Activity className="h-5 w-5 mr-2 text-slate-600" />
              <h3 className="font-semibold">Current Unit System: {unit === UNITS.IMPERIAL ? 'Imperial (ft/in)' : 'Metric (m/cm)'}</h3>
            </div>
            <p className="text-slate-600 text-sm">
              Assumes compacted asphalt pavement unit weight: <span className="font-semibold">110 LB/(SY*INCH)</span>
            </p>
          </div>
          
          <form onSubmit={handleCalculate}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <InputField 
                id="width"
                label="Width"
                value={width}
                onChange={(e) => setWidth(e.target.value)}
                placeholder={`Enter width`}
                unit={unit === UNITS.IMPERIAL ? 'feet' : 'meters'}
                icon={<Ruler className="h-5 w-5" />}
                error={formErrors.width}
              />
              
              <InputField 
                id="length"
                label="Length"
                value={length}
                onChange={(e) => setLength(e.target.value)}
                placeholder={`Enter length`}
                unit={unit === UNITS.IMPERIAL ? 'feet' : 'meters'}
                icon={<Ruler className="h-5 w-5" />}
                error={formErrors.length}
              />
              
              <InputField 
                id="thickness"
                label="Thickness"
                value={thickness}
                onChange={(e) => setThickness(e.target.value)}
                placeholder={`Enter thickness`}
                unit={unit === UNITS.IMPERIAL ? 'inches' : 'centimeters'}
                icon={<Ruler className="h-5 w-5" />}
                error={formErrors.thickness}
              />
            </div>
            
            <div className="flex flex-col sm:flex-row justify-center gap-4 mb-8">
              <button
                type="submit"
                className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-6 rounded-md font-medium transition-colors duration-300 flex items-center justify-center"
              >
                <CalculatorIcon className="h-5 w-5 mr-2" />
                Calculate
              </button>
              
              <button
                type="button"
                onClick={handleClear}
                className="bg-slate-200 hover:bg-slate-300 text-slate-700 py-2 px-6 rounded-md font-medium transition-colors duration-300 flex items-center justify-center"
              >
                <RefreshCw className="h-5 w-5 mr-2" />
                Clear
              </button>
            </div>
          </form>
          
          <CalculatorResults tonnage={tonnage} hasCalculated={hasCalculated} unit={unit} />
        </div>
      </div>
      
      {history.length > 0 && (
        <div className="mt-8">
          <CalculationHistory calculations={history} />
        </div>
      )}
      
      <div className="mt-12 bg-white rounded-xl shadow-lg p-6">
        <h3 className="text-xl font-bold text-slate-800 mb-4">How to Use the Asphalt Calculator</h3>
        <ol className="list-decimal pl-5 space-y-3 text-slate-700">
          <li>Enter the <strong>width</strong> of your asphalt area in {unit === UNITS.IMPERIAL ? 'feet' : 'meters'}</li>
          <li>Enter the <strong>length</strong> of your asphalt area in {unit === UNITS.IMPERIAL ? 'feet' : 'meters'}</li>
          <li>Enter the <strong>thickness</strong> of your asphalt pavement in {unit === UNITS.IMPERIAL ? 'inches' : 'centimeters'}</li>
          <li>Click the <strong>Calculate</strong> button or press <strong>Enter</strong> to get your tonnage estimate</li>
          <li>Use the <strong>Clear</strong> button to reset the calculator for a new calculation</li>
          <li>Toggle between Imperial and Metric units using the unit switch button</li>
        </ol>
        <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-100">
          <p className="text-slate-700 text-sm">
            <strong>Note:</strong> This calculator provides an estimate based on standard asphalt density. 
            Actual requirements may vary based on specific asphalt mix designs, compaction rates, and local conditions.
            For precise estimates, consult with a professional paving contractor.
          </p>
        </div>
      </div>
    </div>
  );
};